<template>
    <div class="comment-collapsible-wrap">
        <div
            class="comment-collapsible"
            :class="{ 'is-collapsed': isCollapsed }"
            :style="isCollapsed ? { maxHeight: maxHeight + 'px' } : {}"
        >
            <div ref="innerEl" class="comment-collapsible-inner">
                <div v-if="isHtml(body)" class="md" v-html="body" />
                <vue-markdown
                    v-else
                    :html="false"
                    :task-lists="false"
                    :anchorAttributes="{ target: '_blank' }"
                    class="md"
                    >{{ body }}</vue-markdown
                >
            </div>
        </div>

        <button v-if="overflowing" type="button" class="comment-collapse-toggle" @click="expanded = !expanded">
            {{ expanded ? $t('Show less') : $t('Show more') }}
        </button>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';

const props = defineProps({
    body: { type: String, default: '' },
    // Height (px) beyond which a comment is clamped. ~15 lines of text.
    maxHeight: { type: Number, default: 300 }
});

const innerEl = ref(null);
const overflowing = ref(false);
const expanded = ref(false);

const isCollapsed = computed(() => overflowing.value && !expanded.value);

function isHtml(content) {
    return /^<[a-z]/i.test(content?.trim() ?? '');
}

function measure() {
    const el = innerEl.value;
    if (!el) {
        return;
    }
    // A small tolerance avoids a toggle that only reveals a few extra pixels.
    overflowing.value = el.scrollHeight > props.maxHeight + 24;
}

let observer = null;

onMounted(() => {
    measure();
    // Images and other async content change the height after the first paint,
    // so keep the overflow decision in sync as the body settles.
    observer = new ResizeObserver(measure);
    if (innerEl.value) {
        observer.observe(innerEl.value);
    }
});

onBeforeUnmount(() => {
    observer?.disconnect();
});

watch(
    () => props.body,
    () => {
        expanded.value = false;
        nextTick(measure);
    }
);
</script>

<style scoped>
.comment-collapsible-wrap {
    margin-top: 0.25rem;
}

.comment-collapsible {
    position: relative;
}

.comment-collapsible.is-collapsed {
    overflow: hidden;
}

/* Fade the clamped content into the background so it reads as "there's more". */
.comment-collapsible.is-collapsed::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 3rem;
    background: linear-gradient(to bottom, rgba(var(--bulma-scheme-main-rgb), 0), var(--bulma-scheme-main));
    pointer-events: none;
}

.comment-collapse-toggle {
    display: inline-block;
    margin-top: 0.35rem;
    padding: 0;
    border: none;
    background: none;
    color: var(--bulma-primary);
    font-size: 0.78rem;
    font-weight: 600;
    cursor: pointer;
}

.comment-collapse-toggle:hover {
    text-decoration: underline;
}

/* Rendered rich text (v-html / markdown): Bulma resets headings and lists, so
   restore the same styling the editor shows while composing. */
.comment-collapsible-inner :deep(.md h1) {
    font-size: 1.4em;
    font-weight: 700;
    line-height: 1.3;
    margin: 0.3em 0;
}
.comment-collapsible-inner :deep(.md h2) {
    font-size: 1.2em;
    font-weight: 600;
    line-height: 1.3;
    margin: 0.3em 0;
}
.comment-collapsible-inner :deep(.md h3) {
    font-size: 1.05em;
    font-weight: 600;
    line-height: 1.3;
    margin: 0.25em 0;
}
.comment-collapsible-inner :deep(.md ul) {
    list-style: disc;
    padding-left: 1.2em;
    margin: 0.2em 0;
}
.comment-collapsible-inner :deep(.md ol) {
    list-style: decimal;
    padding-left: 1.2em;
    margin: 0.2em 0;
}
.comment-collapsible-inner :deep(.md blockquote) {
    border-left: 3px solid var(--bulma-border);
    padding-left: 0.75em;
    color: var(--bulma-grey);
    margin: 0.25em 0;
}
.comment-collapsible-inner :deep(.md img) {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
}
.comment-collapsible-inner :deep(.md table) {
    border-collapse: collapse;
    margin: 0.4em 0;
}
.comment-collapsible-inner :deep(.md th),
.comment-collapsible-inner :deep(.md td) {
    border: 1px solid var(--bulma-border);
    padding: 4px 8px;
}
.comment-collapsible-inner :deep(.md th) {
    background: var(--bulma-scheme-main-bis);
    font-weight: 600;
}
.comment-collapsible-inner :deep(.md .file-attachment) {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    max-width: 320px;
    padding: 0.4rem 0.65rem;
    margin: 0.2em 0;
    border: 1px solid var(--bulma-border);
    border-radius: 8px;
    background: var(--bulma-scheme-main-bis);
    text-decoration: none;
    color: var(--bulma-text);
}
.comment-collapsible-inner :deep(.md .file-attachment:hover) {
    background: var(--bulma-scheme-main-ter);
    border-color: var(--bulma-primary);
}
.comment-collapsible-inner :deep(.md .file-attachment-icon) {
    font-size: 1.35rem;
    line-height: 1;
    color: var(--bulma-primary);
    flex-shrink: 0;
}
.comment-collapsible-inner :deep(.md .file-attachment-info) {
    display: flex;
    flex-direction: column;
    min-width: 0;
    line-height: 1.2;
}
.comment-collapsible-inner :deep(.md .file-attachment-name) {
    font-size: 0.82rem;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.comment-collapsible-inner :deep(.md .file-attachment-size) {
    font-size: 0.7rem;
    color: var(--bulma-text-weak);
}

.comment-collapsible-inner :deep(.md .mention) {
    display: inline-block;
    padding: 0 0.3em;
    border-radius: 4px;
    font-weight: 600;
    color: var(--bulma-primary);
    background: rgba(var(--bulma-primary-rgb), 0.1);
    text-decoration: none;
}
</style>
